import React, { useState, useEffect } from 'react';
import { 
  Lock, Sparkles, Copy, CheckCircle2, RefreshCw, Wand2, ChevronDown, ChevronUp,
  Briefcase, Coffee, Scissors, Home, ShoppingBag, Heart, GraduationCap, Building2,
  Palette, Users, Smile, TrendingUp, Star, Zap, MessageSquare, BookOpen, Award,
  Instagram, Facebook, Youtube, Monitor, Image as ImageIcon, Layers, Settings,
  Utensils, Plane, Dumbbell, Tv, User, Package, Square, RectangleVertical, RectangleHorizontal,
  Grid2X2, LayoutGrid, Loader2, X, Grid3x3, Columns3, Rows3, Gift, Calendar,
  Megaphone, Trophy, Camera, FileText, Tag, Lightbulb, Target, Smartphone
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../contexts/AuthContext';

interface SocialAccount {
  platform: string;
  username: string;
}

interface BrandDNA {
  brandCategory: string;
  brandStyles: string[];
  colors: string[];
  targetAudience: string;
  targetGender: string;
  contactLine?: string;
  contactPhone?: string;
  socialAccounts?: SocialAccount[];
}

const CoursedeeAIV2: React.FC = () => {
  const { user } = useAuthContext();
  
  useEffect(() => {
    document.title = 'AIForYou V2';
  }, []);

  // User display name from Firebase Auth
  const currentUsername = user?.email?.split('@')[0] || user?.displayName || 'User';


  // Load user data from Firestore
  const loadUserData = async (username: string) => {
    try {
      setIsLoadingBrandDNA(true);
      console.log('🔍 [V2 Load] Loading Brand DNA for username:', username);
      
      const userDocRef = doc(db, 'coursedee_v2_users', username);
      const userDoc = await getDoc(userDocRef);
      
      console.log('📦 [V2 Firestore] Document exists:', userDoc.exists());
      
      if (userDoc.exists()) {
        const data = userDoc.data() as BrandDNA;
        console.log('✅ [V2 Brand DNA Found]:', data);
        setBrandDNA(data);
        setHasBrandDNA(true);
        setShowBrandSetup(false);
      } else {
        console.log('❌ [V2 No Document] Firestore document does not exist');
        
        // Try to restore from localStorage backup
        const localBackup = localStorage.getItem(`coursedee_v2_brand_dna_${username}`);
        if (localBackup) {
          console.log('🔄 [V2 Recovery] Found backup in localStorage, restoring...');
          try {
            const parsed = JSON.parse(localBackup) as BrandDNA;
            setBrandDNA(parsed);
            setHasBrandDNA(true);
            setShowBrandSetup(false);
            
            // Re-save to Firestore
            await setDoc(userDocRef, parsed, { merge: true });
            console.log('✅ [V2 Recovery] Restored and re-saved to Firestore');
          } catch (e) {
            console.error('❌ [V2 Recovery] Failed to restore from localStorage:', e);
            setShowBrandSetup(true);
          }
        } else {
          console.log('❌ [V2 No Backup] No localStorage backup found');
          setShowBrandSetup(true);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setShowBrandSetup(true);
    } finally {
      setIsLoadingBrandDNA(false);
    }
  };

  // Load Brand DNA on mount
  useEffect(() => {
    if (currentUsername) {
      loadUserData(currentUsername);
    } else {
      setIsLoadingBrandDNA(false);
    }
  }, [currentUsername]);


  const [hasBrandDNA, setHasBrandDNA] = useState(false);
  const [showBrandSetup, setShowBrandSetup] = useState(false);
  const [isLoadingBrandDNA, setIsLoadingBrandDNA] = useState(true);
  const [brandDNA, setBrandDNA] = useState<BrandDNA>({
    brandCategory: '',
    brandStyles: [],
    colors: ['#FF69B4', '#808080'],
    targetAudience: '',
    targetGender: 'all',
    contactLine: '',
    contactPhone: '',
    socialAccounts: []
  });
  const [showColorPicker, setShowColorPicker] = useState<number | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [socialUsername, setSocialUsername] = useState('');

  const [selectedSocialPlatform, setSelectedSocialPlatform] = useState('');
  const [platform, setPlatform] = useState('square');
  const [templateType, setTemplateType] = useState('');
  const [contentMode, setContentMode] = useState<'quick' | 'full'>('quick');
  const [quickTopic, setQuickTopic] = useState('');
  const [fullContent, setFullContent] = useState('');
  const [headline, setHeadline] = useState('');
  const [subtext, setSubtext] = useState('');
  const [cta, setCta] = useState('');
  
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [noAIModel, setNoAIModel] = useState(false);
  const [hideContact, setHideContact] = useState(false);
  const [variationMode, setVariationMode] = useState('consistent');
  const [humanControl, setHumanControl] = useState('include');
  const [faceReference, setFaceReference] = useState(false);
  const [productReference, setProductReference] = useState(false);
  const [logoReference, setLogoReference] = useState(false);
  const [creativeStrength, setCreativeStrength] = useState('balanced');
  const [styleOverride, setStyleOverride] = useState('');

  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedContent, setLastGeneratedContent] = useState('');
  const [lastNoAIModel, setLastNoAIModel] = useState(false);

  const brandCategories = [
    { id: 'beauty', label: 'Beauty', icon: Heart, color: 'from-pink-500 to-rose-500' },
    { id: 'business', label: 'Business', icon: Briefcase, color: 'from-blue-500 to-cyan-500' },
    { id: 'knowledge', label: 'Knowledge', icon: GraduationCap, color: 'from-purple-500 to-indigo-500' },
    { id: 'travel', label: 'Travel', icon: Plane, color: 'from-cyan-500 to-blue-500' },
    { id: 'food', label: 'Food', icon: Utensils, color: 'from-orange-500 to-amber-500' },
    { id: 'health', label: 'Health', icon: Dumbbell, color: 'from-green-500 to-emerald-500' },
    { id: 'entertainment', label: 'Entertainment', icon: Tv, color: 'from-red-500 to-pink-500' },
    { id: 'personal', label: 'Personal Brand', icon: User, color: 'from-violet-500 to-purple-500' },
    { id: 'product', label: 'Product', icon: Package, color: 'from-amber-500 to-yellow-500' }
  ];

  const brandStyles = [
    { id: 'luxury', label: 'Luxury', color: 'from-purple-600 to-pink-600' },
    { id: 'friendly', label: 'Friendly', color: 'from-green-400 to-emerald-400' },
    { id: 'minimal', label: 'Minimal', color: 'from-slate-500 to-gray-400' },
    { id: 'bold', label: 'Bold', color: 'from-red-500 to-orange-500' },
    { id: 'modern', label: 'Modern', color: 'from-blue-500 to-cyan-500' },
    { id: 'professional', label: 'Professional', color: 'from-indigo-600 to-blue-600' },
    { id: 'cinematic', label: 'Cinematic', color: 'from-violet-600 to-purple-600' },
    { id: 'playful', label: 'Playful', color: 'from-pink-400 to-rose-400' },
    { id: 'premium', label: 'Premium', color: 'from-amber-600 to-yellow-600' },
    { id: 'trustworthy', label: 'Trustworthy', color: 'from-teal-500 to-cyan-500' }
  ];

  const targetAudiences = [
    { id: 'general', label: 'General' },
    { id: 'kids', label: 'Kids' },
    { id: 'young', label: 'Young' },
    { id: 'professional', label: 'Professional' },
    { id: 'premium', label: 'Premium' },
    { id: 'senior', label: 'Senior' }
  ];

  const targetGenders = [
    { id: 'all', label: 'All' },
    { id: 'women', label: 'Women' },
    { id: 'men', label: 'Men' }
  ];

  const socialPlatforms = [
    { id: 'facebook', label: 'Facebook', shortLabel: 'FB' },
    { id: 'instagram', label: 'Instagram', shortLabel: 'IG' },
    { id: 'tiktok', label: 'TikTok', shortLabel: 'TikTok' },
    { id: 'youtube', label: 'YouTube', shortLabel: 'YT' },
    { id: 'x', label: 'X (Twitter)', shortLabel: 'X' },
    { id: 'linkedin', label: 'LinkedIn', shortLabel: 'LinkedIn' },
    { id: 'threads', label: 'Threads', shortLabel: 'Threads' },
    { id: 'line', label: 'Line Official', shortLabel: 'Line' }
  ];

  const togglePlatform = (platformId: string) => {
    if (selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platformId));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platformId]);
    }
  };

  const addSocialAccount = () => {
    if (selectedPlatforms.length === 0 || !socialUsername.trim()) {
      alert('กรุณาเลือก Platform อย่างน้อย 1 ตัว และใส่ชื่อ');
      return;
    }

    const newAccounts: SocialAccount[] = selectedPlatforms.map(platform => ({
      platform,
      username: socialUsername.trim()
    }));

    setBrandDNA({
      ...brandDNA,
      socialAccounts: [...(brandDNA.socialAccounts || []), ...newAccounts]
    });

    setSelectedPlatforms([]);
    setSocialUsername('');
  };

  const removeSocialAccount = (index: number) => {
    setBrandDNA({
      ...brandDNA,
      socialAccounts: brandDNA.socialAccounts?.filter((_, i) => i !== index) || []
    });
  };

  const formats = [
    // Social
    { id: 'square', label: 'Square', ratio: '1:1', category: 'social', icon: Square, description: 'Instagram, Facebook' },
    { id: 'portrait', label: 'Portrait', ratio: '4:5', category: 'social', icon: RectangleVertical, description: 'Instagram Feed' },
    { id: 'landscape', label: 'Landscape', ratio: '16:9', category: 'social', icon: RectangleHorizontal, description: 'Twitter, LinkedIn' },
    // Story / Short Video
    { id: 'story', label: 'Story / Reels', ratio: '9:16', category: 'story', icon: RectangleVertical, description: 'Instagram, TikTok' },
    // Banner
    { id: 'hero', label: 'Hero Banner', ratio: '16:9', category: 'banner', icon: RectangleHorizontal, description: 'Website Header' },
    { id: 'facebook-cover', label: 'Facebook Cover', ratio: '820:312', category: 'banner', icon: RectangleHorizontal, description: 'Facebook Page' },
    // Video Cover
    { id: 'youtube', label: 'YouTube Thumbnail', ratio: '16:9', category: 'video', icon: RectangleHorizontal, description: 'YouTube Video' },
    { id: 'tiktok-cover', label: 'TikTok Cover', ratio: '1:1', category: 'video', icon: Square, description: 'TikTok Profile' }
  ];

  const advancedFormats = [
    // Album 3 ภาพ
    { id: 'album-1-2', label: 'Album 1+2', ratio: 'multi', category: 'advanced', icon: LayoutGrid, description: '1 ใหญ่ + 2 เล็ก', layout: 'horizontal' },
    { id: 'album-2-1', label: 'Album 2+1', ratio: 'multi', category: 'advanced', icon: Rows3, description: '2 บน + 1 ล่าง', layout: 'vertical' },
    { id: 'album-1-2v', label: 'Album 1+2 Vertical', ratio: 'multi', category: 'advanced', icon: Columns3, description: '1 ซ้าย + 2 ขวา', layout: 'vertical' },
    { id: 'album-3-row', label: 'Album 3 Rows', ratio: 'multi', category: 'advanced', icon: Rows3, description: '3 แถวตั้ง', layout: 'vertical' },
    { id: 'album-1-2-side', label: 'Album 1+2 Sides', ratio: 'multi', category: 'advanced', icon: Columns3, description: '1 ใหญ่ + 2 ข้าง', layout: 'horizontal' },
    
    // Album 4-5 ภาพ
    { id: 'album-grid-4', label: 'Grid 2×2', ratio: 'multi', category: 'advanced', icon: Grid2X2, description: '4 ช่องเท่ากัน', layout: 'square' },
    { id: 'album-1-3', label: 'Album 1+3', ratio: 'multi', category: 'advanced', icon: Grid3x3, description: '1 ใหญ่ + 3 เล็ก', layout: 'horizontal' },
    { id: 'album-1-3-bottom', label: 'Album 1+3 Bottom', ratio: 'multi', category: 'advanced', icon: Rows3, description: '1 บน + 3 ล่าง', layout: 'vertical' },
    { id: 'album-2-3', label: 'Album 2+3', ratio: 'multi', category: 'advanced', icon: Grid3x3, description: '2 ซ้าย + 3 ขวา', layout: 'horizontal' },
    
    // Instagram & Others
    { id: 'carousel', label: 'Carousel', ratio: 'multi', category: 'advanced', icon: Layers, description: 'Instagram Carousel', layout: 'horizontal' },
    { id: 'multi-grid', label: 'Multi-Post Grid', ratio: 'multi', category: 'advanced', icon: Grid3x3, description: 'Instagram Grid', layout: 'square' }
  ];

  const [showAdvancedFormats, setShowAdvancedFormats] = useState(false);

  const contentPlatforms = [
    { id: 'facebook', label: 'Facebook', icon: Users },
    { id: 'instagram', label: 'Instagram', icon: Camera },
    { id: 'tiktok', label: 'TikTok', icon: Smartphone },
    { id: 'youtube', label: 'YouTube', icon: Monitor },
    { id: 'lemon8', label: 'Lemon8', icon: Sparkles },
    { id: 'x', label: 'X', icon: MessageSquare }
  ];

  const platformFormats: { [key: string]: typeof formats } = {
    facebook: [
      { id: 'square', label: 'Square Post', ratio: '1:1', category: 'social', icon: Square, description: 'โพสต์ทั่วไป เหมาะกับฟีด Facebook' },
      { id: 'portrait', label: 'Portrait', ratio: '4:5', category: 'social', icon: RectangleVertical, description: 'ฟีด Facebook แนวตั้ง ดูเต็มหน้าจอมือถือ' },
      { id: 'landscape', label: 'Landscape', ratio: '16:9', category: 'social', icon: RectangleHorizontal, description: 'ภาพแนวนอน เหมาะกับการแชร์ลิงก์' },
      { id: 'facebook-cover', label: 'Cover Photo', ratio: '820:312', category: 'banner', icon: RectangleHorizontal, description: 'ภาพปกเพจ Facebook' },
      { id: 'story', label: 'Story', ratio: '9:16', category: 'story', icon: RectangleVertical, description: 'สตอรี่ Facebook แนวตั้ง' }
    ],
    instagram: [
      { id: 'square', label: 'Square Post', ratio: '1:1', category: 'social', icon: Square, description: 'โพสต์ IG แบบสี่เหลี่ยมจัตุรัส' },
      { id: 'portrait', label: 'Portrait', ratio: '4:5', category: 'social', icon: RectangleVertical, description: 'ฟีด IG แนวตั้ง ดูเต็มหน้าจอมือถือ' },
      { id: 'story', label: 'Story/Reels', ratio: '9:16', category: 'story', icon: RectangleVertical, description: 'สตอรี่และรีลส์ Instagram' },
      { id: 'carousel', label: 'Carousel', ratio: 'multi', category: 'advanced', icon: Layers, description: 'โพสต์หลายภาพ เลื่อนดูได้' }
    ],
    tiktok: [
      { id: 'story', label: 'Video', ratio: '9:16', category: 'story', icon: RectangleVertical, description: 'วิดีโอ TikTok แนวตั้งเต็มจอ' },
      { id: 'tiktok-cover', label: 'Profile Cover', ratio: '1:1', category: 'video', icon: Square, description: 'ภาพปกโปรไฟล์ TikTok' }
    ],
    youtube: [
      { id: 'youtube', label: 'Thumbnail', ratio: '16:9', category: 'video', icon: RectangleHorizontal, description: 'ภาพปกวิดีโอ YouTube' },
      { id: 'youtube-banner', label: 'Channel Banner', ratio: '2560:1800', category: 'banner', icon: RectangleHorizontal, description: 'ปกช่อง YouTube (แสดงเต็มจอ)' },
      { id: 'youtube-banner-safe', label: 'Banner Safe Area', ratio: '1546:423', category: 'banner', icon: RectangleHorizontal, description: 'ปกช่อง YouTube (พื้นที่ปลอดภัย)' }
    ],
    lemon8: [
      { id: 'portrait', label: 'Portrait', ratio: '4:5', category: 'social', icon: RectangleVertical, description: 'โพสต์ Lemon8 แนวตั้ง' },
      { id: 'square', label: 'Square', ratio: '1:1', category: 'social', icon: Square, description: 'โพสต์ Lemon8 แบบสี่เหลี่ยม' }
    ],
    x: [
      { id: 'landscape', label: 'Post', ratio: '16:9', category: 'social', icon: RectangleHorizontal, description: 'โพสต์ X (Twitter) แนวนอน' },
      { id: 'square', label: 'Square', ratio: '1:1', category: 'social', icon: Square, description: 'โพสต์ X (Twitter) แบบสี่เหลี่ยม' }
    ]
  };

  const templateTypes = [
    { id: 'promotion', label: 'Promotion', icon: Zap, color: 'from-orange-500 to-amber-500', description: 'ภาพโปรโมชั่น ลดราคา ขายของ' },
    { id: 'educational', label: 'Educational', icon: BookOpen, color: 'from-blue-500 to-cyan-500', description: 'ภาพให้ความรู้ สอน แชร์เทคนิค' },
    { id: 'review', label: 'Review', icon: Star, color: 'from-yellow-500 to-amber-500', description: 'ภาพรีวิว เรตติ้ง คำชมจากลูกค้า' },
    { id: 'before-after', label: 'Before/After', icon: Layers, color: 'from-purple-500 to-pink-500', description: 'ภาพก่อน-หลัง แสดงผลลัพธ์' },
    { id: 'launch', label: 'Launch', icon: Sparkles, color: 'from-green-500 to-emerald-500', description: 'ภาพเปิดตัวสินค้า/บริการใหม่' },
    { id: 'advertisement', label: 'Advertisement', icon: TrendingUp, color: 'from-red-500 to-rose-500', description: 'ภาพโฆษณา ประชาสัมพันธ์' },
    { id: 'quote', label: 'Branding Quote', icon: MessageSquare, color: 'from-indigo-500 to-purple-500', description: 'ภาพคำคม สร้างแบรนด์ สร้างแรงบันดาลใจ' },
    { id: 'event', label: 'Event', icon: Calendar, color: 'from-violet-500 to-fuchsia-500', description: 'ภาพประกาศอีเวนต์ กิจกรรม งานพิเศษ' },
    { id: 'announcement', label: 'Announcement', icon: Megaphone, color: 'from-pink-500 to-rose-500', description: 'ภาพประกาศข่าวสาร อัพเดทใหม่' },
    { id: 'contest', label: 'Contest', icon: Trophy, color: 'from-amber-500 to-orange-500', description: 'ภาพประกวด แข่งขัน ชิงรางวัล' },
    { id: 'lifestyle', label: 'Lifestyle', icon: Camera, color: 'from-teal-500 to-cyan-500', description: 'ภาพไลฟ์สไตล์ บรรยากาศ วิถีชีวิต' },
    { id: 'infographic', label: 'Infographic', icon: FileText, color: 'from-blue-600 to-indigo-600', description: 'ภาพอินโฟกราฟิก ข้อมูลสถิติ' },
    { id: 'flash-sale', label: 'Flash Sale', icon: Tag, color: 'from-red-600 to-pink-600', description: 'ภาพลดแรง เซลล์ด่วน จำกัดเวลา' },
    { id: 'tips', label: 'Tips & Tricks', icon: Lightbulb, color: 'from-yellow-400 to-amber-400', description: 'ภาพเคล็ดลับ ทริค ไอเดีย' },
    { id: 'testimonial', label: 'Testimonial', icon: Users, color: 'from-emerald-500 to-green-500', description: 'ภาพคำรับรอง ความประทับใจจากลูกค้า' },
    { id: 'product-focus', label: 'Product Focus', icon: Target, color: 'from-indigo-600 to-purple-600', description: 'ภาพเน้นสินค้า ไฮไลท์ฟีเจอร์' },
    { id: 'giveaway', label: 'Giveaway', icon: Gift, color: 'from-fuchsia-500 to-pink-500', description: 'ภาพแจกของรางวัล กิจกรรมแจก' }
  ];

  const toggleBrandStyle = (styleId: string) => {
    const currentStyles = brandDNA.brandStyles;
    if (currentStyles.includes(styleId)) {
      setBrandDNA({...brandDNA, brandStyles: currentStyles.filter(s => s !== styleId)});
    } else {
      if (currentStyles.length < 2) {
        setBrandDNA({...brandDNA, brandStyles: [...currentStyles, styleId]});
      } else {
        alert('คุณสามารถเลือกได้สูงสุด 2 แบบเท่านั้น');
      }
    }
  };

  const saveBrandDNA = async () => {
    if (!brandDNA.brandCategory || brandDNA.brandStyles.length === 0 || brandDNA.colors.length === 0 || 
        !brandDNA.targetAudience) {
      alert('กรุณากรอกข้อมูล Brand DNA ให้ครบถ้วน');
      return;
    }
    if (brandDNA.brandStyles.length > 2) {
      alert('กรุณาเลือก Brand Style ไม่เกิน 2 แบบ');
      return;
    }

    if (selectedPlatforms.length > 0 || socialUsername.trim()) {
      alert('⚠️ คุณเลือก Platform หรือใส่ชื่อไว้แล้ว กรุณากดปุ่ม "เพิ่ม" ก่อนบันทึก Brand DNA');
      return;
    }

    try {
      console.log('💾 [V2 Save] Starting save for username:', currentUsername);
      console.log('📦 [V2 Save] Brand DNA to save:', brandDNA);
      
      const userDocRef = doc(db, 'coursedee_v2_users', currentUsername);
      await setDoc(userDocRef, brandDNA, { merge: true });
      
      console.log('✅ [V2 Save] Saved to Firestore successfully');
      
      // Verify the save by reading it back
      const verifyDoc = await getDoc(userDocRef);
      if (verifyDoc.exists()) {
        console.log('✅ [V2 Verify] Data verified in Firestore');
      } else {
        console.error('❌ [V2 Verify] Data NOT found in Firestore after save!');
        throw new Error('Failed to verify saved data');
      }
      
      // Also save to localStorage as backup
      localStorage.setItem(`coursedee_v2_brand_dna_${currentUsername}`, JSON.stringify(brandDNA));
      console.log('✅ [V2 Backup] Saved to localStorage');
      
      setHasBrandDNA(true);
      setShowBrandSetup(false);
      alert('✅ บันทึก Brand DNA สำเร็จ!');
    } catch (error) {
      console.error('Error saving Brand DNA:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`❌ เกิดข้อผิดพลาดในการบันทึก Brand DNA: ${errorMessage}`);
    }
  };

  const addColor = () => {
    if (brandDNA.colors.length < 6) {
      setBrandDNA({...brandDNA, colors: [...brandDNA.colors, '#808080']});
    }
  };

  const updateColor = (index: number, color: string) => {
    const newColors = [...brandDNA.colors];
    newColors[index] = color;
    setBrandDNA({...brandDNA, colors: newColors});
  };

  const removeColor = (index: number) => {
    if (brandDNA.colors.length > 1) {
      const newColors = brandDNA.colors.filter((_, i) => i !== index);
      setBrandDNA({...brandDNA, colors: newColors});
    }
  };

  const editBrandDNA = () => {
    setShowBrandSetup(true);
    setHasBrandDNA(false);
  };

  const generatePrompt = async () => {
    if (!platform || !templateType || !fullContent) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    // ตรวจสอบว่ากด Generate ซ้ำโดยไม่เปลี่ยนเนื้อหาหรือการตั้งค่าหรือไม่
    if (generatedPrompt && lastGeneratedContent === fullContent && lastNoAIModel === noAIModel) {
      alert('⚠️ คุณได้ Prompt สำหรับหัวข้อนี้แล้ว\n\nกรุณาเปลี่ยนหัวข้อใหม่หรือแก้ไขเนื้อหา/การตั้งค่าก่อนสร้าง Prompt ใหม่');
      return;
    }

    setIsGenerating(true);
    
    // Simulate AI processing time
    await new Promise(resolve => setTimeout(resolve, 1500));

    const selectedFormat = [...formats, ...advancedFormats].find(f => f.id === platform);
    const selectedTemplate = templateTypes.find(t => t.id === templateType);
    const selectedCategory = brandCategories.find(b => b.id === brandDNA.brandCategory);
    const selectedStyles = brandStyles.filter(s => brandDNA.brandStyles.includes(s.id));
    const styleLabels = selectedStyles.map(s => s.label).join(' + ');

    const contentToAnalyze = fullContent;

    const stylePreset: Record<string, string> = {
      'luxury': 'luxury premium aesthetic, elegant sophisticated composition, high-end refined style',
      'friendly': 'friendly approachable vibe, warm welcoming atmosphere, relatable human touch',
      'minimal': 'minimal clean design, simple modern layout, professional white space',
      'bold': 'bold dramatic impact, strong confident presence, eye-catching powerful visuals',
      'modern': 'modern contemporary style, fresh innovative design, cutting-edge aesthetic',
      'professional': 'professional polished look, trustworthy credible presence, business-appropriate',
      'cinematic': 'cinematic storytelling, dramatic lighting, emotional depth, film-like quality',
      'playful': 'playful fun energy, cheerful lighthearted mood, creative joyful expression',
      'premium': 'premium exclusive feel, upscale sophisticated quality, luxury positioning',
      'trustworthy': 'trustworthy reliable presence, authentic genuine feel, dependable credible image'
    };

    const combinedStyleDescription = selectedStyles.map(s => stylePreset[s.id]).join(', ');

    const templatePreset: Record<string, string> = {
      'promotion': 'promotional marketing visual, attention-grabbing design, clear value proposition, sales-focused layout',
      'educational': 'educational informative design, clear knowledge delivery, easy-to-understand visual hierarchy',
      'review': 'authentic review showcase, testimonial-focused, trust-building elements, social proof design',
      'before-after': 'before and after transformation, split comparison layout, dramatic results showcase',
      'launch': 'exciting product launch visual, announcement energy, new release excitement, debut presentation',
      'advertisement': 'professional advertisement design, commercial quality, brand-focused messaging, marketing excellence',
      'quote': 'inspirational branding quote, typography-focused design, motivational message, brand voice expression',
      'event': 'event announcement design, festive engaging atmosphere, date and time prominence, excitement building visual',
      'announcement': 'important announcement visual, news-worthy presentation, clear message delivery, attention-commanding design',
      'contest': 'contest and competition design, prize showcase, participation excitement, competitive energy, winner-focused visual',
      'lifestyle': 'lifestyle aesthetic photography, authentic moment capture, relatable everyday scenes, aspirational living visual',
      'infographic': 'infographic data visualization, clear statistics presentation, easy-to-digest information, educational chart design',
      'flash-sale': 'urgent flash sale design, time-limited pressure, dramatic discount showcase, immediate action-driving visual',
      'tips': 'helpful tips and tricks design, practical advice presentation, easy-to-follow guidance, valuable insight sharing',
      'testimonial': 'customer testimonial showcase, authentic voice presentation, trust-building social proof, real experience highlight',
      'product-focus': 'product-focused hero shot, feature highlight design, benefit-driven presentation, detailed showcase visual',
      'giveaway': 'exciting giveaway announcement, prize attraction design, participation encouragement, generous offering showcase'
    };

    const platformPreset: Record<string, string> = {
      'square': `square 1:1 aspect ratio optimized for social media feeds, balanced composition`,
      'portrait': `portrait 4:5 aspect ratio optimized for Instagram feed, vertical emphasis`,
      'landscape': `landscape 16:9 aspect ratio optimized for social platforms, wide composition`,
      'story': `vertical 9:16 aspect ratio optimized for Stories and Reels, mobile-first design`,
      'hero': `wide 16:9 aspect ratio for hero banner, desktop-optimized, panoramic composition`,
      'facebook-cover': `820:312 aspect ratio for Facebook cover, wide banner format`,
      'youtube': `wide 16:9 aspect ratio for YouTube thumbnail, click-worthy design, bold visual`,
      'youtube-banner': `⚠️ CRITICAL: YouTube Channel Banner - Extended Vertical Layout Design

**CANVAS SIZE:**
- Exact dimensions: 1493 x 1053 px (extended height for cropping flexibility)
- ❗ This is intentionally TALLER for vertical repositioning

**DESIGN PHILOSOPHY:**
- Create "extended vertical layout" with extra space top and bottom
- Design is meant to be TALLER than standard (1800 vs 1440)
- ❗ DO NOT compress or squeeze elements to fit 1440 height
- ❗ KEEP the "too tall" aesthetic - this is intentional
- Must be croppable to 2560x1440 without losing core elements

**SAFE AREA (900 x 246 px - ABSOLUTE CENTER):**
❗ CRITICAL TEXT REQUIREMENTS:
- ALL text content (title, subtitle, tagline) MUST fit within this area
- Position at vertical center of 1053px canvas (around y: 403-649)
- Horizontally centered 100%
- Minimum padding: 80-120px left and right from safe area edges

**TITLE (Main Text) SIZING:**
❗ CRITICAL - Title must be SMALLER and more balanced:
- Text height: maximum 30-35% of safe area height (~74-86px)
- Keep title COMPACT, SMALLER, and READABLE
- Horizontal padding: minimum 50-70px from safe area edges
- Must be readable on ALL devices (Mobile / Desktop / TV)
- Crystal clear, professional typography
- NO distortion, NO blur

**SUBTITLE / TAGLINE:**
- Clearly smaller than title (obvious size difference)
- Appropriate spacing from title (breathing room)
- Must NOT overlap or crowd title
- Maintains readability

**CONTACT INFORMATION DISPLAY:**
❗ IMPORTANT - Display contact info prominently:
- Show Line ID, Phone, and Social Media handles if provided
- Position contact info clearly visible (bottom or side of safe area)
- Use icons for social platforms (Facebook, Instagram, TikTok, YouTube, etc.)
- Make contact info easy to read and professional
- Integrate naturally with overall design

**LAYOUT PRINCIPLES:**
- Breathing space: elements must NOT be cramped
- Balanced composition: easy to read
- Professional hierarchy: clear visual priority
- Comfortable spacing between all elements

**MAIN DESIGN AREA (1310 x 815 px - CENTER):**
- Supporting elements, secondary graphics, decorative elements
- Position at vertical center of canvas
- Ensures visibility across viewing contexts

**EXTENDED TOP & BOTTOM AREAS:**
❗ CRITICAL - These areas MUST be fully designed:
✅ REQUIRED elements:
- Continuous background gradients flowing from center
- Light effects, glows, atmospheric elements
- Graphic elements: patterns, shapes, textures
- Scene continuation matching the theme
- Thematic decorations aligned with user's BRAND DNA

❗ FORBIDDEN:
- Empty/blank space
- Plain solid colors
- Abrupt cuts or edges
- Generic elements that ignore brand identity

**BRAND DNA INTEGRATION:**
❗ CRITICAL - DO NOT use fixed colors/mood/style:
- Adapt colors to user's brand colors
- Match brand style (luxury, friendly, minimal, bold, etc.)
- Reflect brand category (beauty, business, knowledge, etc.)
- Align with target audience preferences
- Flexible design that respects brand identity

**DESIGN QUALITY:**
- Professional, realistic composition
- Balanced throughout entire 1493x1053 canvas
- Seamless transitions between all zones
- Premium lighting and depth
- Croppable without losing core elements

**GOAL:** Create a premium, vertically-extended banner (1493x1053) that:
1. Can be repositioned/cropped as needed
2. Keeps all text within safe area with proper sizing
3. Has breathing space and balanced layout
4. Adapts to user's BRAND DNA (colors, style, category)
5. Maintains professional quality across all devices`,
      'youtube-banner-safe': `⚠️ CRITICAL: YouTube Banner Safe Area - Extended Vertical Layout Design

**CANVAS SIZE:**
- Full canvas: 1493 x 1053 px (extended height for cropping flexibility)
- ❗ This is intentionally TALLER for vertical repositioning

**DESIGN PHILOSOPHY:**
- Create "extended vertical layout" with extra space top and bottom
- Design is meant to be TALLER than standard
- ❗ DO NOT compress or squeeze elements
- ❗ KEEP the "too tall" aesthetic - this is intentional
- Must be croppable without losing core elements

**DESIGN PRIORITY ZONES:**

1. **Safe Area (900 x 246 px - ABSOLUTE CENTER):**
   ❗ CRITICAL TEXT REQUIREMENTS:
   - Position at vertical center of 1053px canvas (around y: 403-649)
   - ALL text content (title, subtitle, tagline) MUST fit here
   - Horizontally centered 100%
   - Minimum padding: 50-70px left and right from safe area edges
   
   **TITLE (Main Text) SIZING:**
   ❗ CRITICAL - Title must be SMALLER and more balanced:
   - Text height: maximum 30-35% of safe area height (~74-86px)
   - Keep title COMPACT, SMALLER, and READABLE
   - Horizontal padding: minimum 50-70px from safe area edges
   - Must be readable on ALL devices (Mobile / Desktop / TV)
   - Crystal clear, professional typography
   
   **SUBTITLE / TAGLINE:**
   - Clearly smaller than title (obvious size difference)
   - Appropriate spacing from title (breathing room)
   - Must NOT overlap or crowd title
   
   **CONTACT INFORMATION:**
   - By keeping title/subtitle SMALLER, contact info (Line ID, Phone, Social Media) will be visible
   - Contact info should appear naturally in remaining space
   - Use icons for social platforms when applicable
   - Make contact info easy to read and professional
   
   **LAYOUT PRINCIPLES:**
   - Breathing space: elements must NOT be cramped
   - Balanced composition: easy to read
   - Professional hierarchy: clear visual priority
   - Smaller text = more room for contact details

2. **Main Area (1310 x 815 px - CENTER):**
   - Supporting elements, secondary graphics, decorative elements
   - Position at vertical center of canvas
   - Ensures visibility across viewing contexts

3. **Extended Top & Bottom:**
   - Continuous design elements matching brand identity

**EXTENDED TOP & BOTTOM AREAS:**
❗ CRITICAL - These areas MUST be fully designed:
✅ REQUIRED elements:
- Continuous background gradients flowing from center
- Light effects, glows, atmospheric elements
- Graphic elements: patterns, shapes, textures
- Scene continuation matching the theme
- Thematic decorations aligned with user's BRAND DNA

❗ FORBIDDEN:
- Empty/blank space
- Plain solid colors
- Abrupt cuts or edges
- Generic elements that ignore brand identity

**BRAND DNA INTEGRATION:**
❗ CRITICAL - DO NOT use fixed colors/mood/style:
- Adapt colors to user's brand colors
- Match brand style (luxury, friendly, minimal, bold, etc.)
- Reflect brand category (beauty, business, knowledge, etc.)
- Align with target audience preferences
- Flexible design that respects brand identity

**DESIGN QUALITY:**
- Professional, realistic composition
- Balanced throughout entire 1493x1053 canvas
- Seamless transitions between all zones
- Premium lighting and shadows
- Croppable without losing core elements

**GOAL:** Create a premium, vertically-extended banner (1493x1053) that:
1. Can be repositioned/cropped as needed
2. Keeps all text within safe area with proper sizing and padding
3. Has breathing space and balanced layout
4. Adapts to user's BRAND DNA (colors, style, category)
5. Maintains professional quality across all devices`,
      'tiktok-cover': `square 1:1 aspect ratio for TikTok profile cover, attention-grabbing`,
      // Album 3 ภาพ
      'album-1-2': `multi-image layout with 1 large + 2 small images, Facebook album style`,
      'album-2-1': `multi-image layout with 2 images on top + 1 large below, Facebook album style`,
      'album-1-2v': `multi-image layout with 1 large left + 2 small right, vertical Facebook album`,
      'album-3-row': `multi-image layout with 3 vertical rows, Facebook album style`,
      'album-1-2-side': `multi-image layout with 1 large center + 2 small sides, Facebook album style`,
      // Album 4-5 ภาพ
      'album-grid-4': `multi-image grid layout 2×2 with 4 equal images, Facebook album style`,
      'album-1-3': `multi-image layout with 1 large + 3 small images, Facebook album style`,
      'album-1-3-bottom': `multi-image layout with 1 large top + 3 small bottom, Facebook album style`,
      'album-2-3': `multi-image layout with 2 images left + 3 images right, Facebook album style`,
      // Instagram & Others
      'carousel': `carousel layout for multiple swipeable images, Instagram carousel format`,
      'multi-grid': `multi-post grid layout for Instagram feed planning, cohesive visual story`
    };

    let negativePrompt = 'blurry, low quality, distorted, deformed, bad anatomy, extra fingers, mutated hands, poorly drawn face, disfigured, ugly, bad proportions, duplicate, watermark, signature, text errors, jpeg artifacts, low resolution';

    // ถ้าไม่ต้องการนางแบบ AI
    if (noAIModel) {
      negativePrompt += ', people, human, person, face, hands, body, characters, models';
    }

    // คำแนะนำสำหรับการแนบภาพ (ใช้เสมอถ้ามีการแนบภาพ)
    let referenceInstructions = '\n\n**📎 หากแนบภาพนางแบบ/สินค้า/โลโก้:**\n';
    referenceInstructions += '- ✅ ใบหน้า/สินค้า/โลโก้ ต้องเหมือนภาพต้นฉบับทุกประการ\n';
    referenceInstructions += '- ✅ อิริยาบถ ท่าทาง และองค์ประกอบอื่นๆ เปลี่ยนไปตามคอนเซ็ปต์ใหม่\n';
    referenceInstructions += '- ✅ สร้างท่าทางใหม่ที่เหมาะกับบริบทของภาพ\n';
    
    // คำแนะนำสำหรับภาพต่อไป
    let nextImageInstructions = '\n\n**🎨 ต้องการสร้างภาพต่อไป?**\n';
    nextImageInstructions += 'พิมพ์: "ภาพต่อไป" เพื่อให้ ChatGPT สร้างภาพใหม่ด้วยสไตล์เดียวกัน\n';

    const creativityPresets: Record<string, string> = {
      'safe': 'safe conservative approach, proven design patterns, minimal risk, traditional composition',
      'balanced': 'balanced creative approach, mix of proven and innovative, moderate experimentation',
      'experimental': 'experimental bold creativity, push boundaries, unique innovative design, artistic freedom'
    };

    const colorPaletteText = brandDNA.colors.map((color, index) => {
      if (index === 0) return `Primary: ${color}`;
      if (index === 1) return `Secondary: ${color}`;
      return `Accent ${index - 1}: ${color}`;
    }).join(', ');

    const genderContext = brandDNA.targetGender !== 'all' ? ` (${brandDNA.targetGender})` : '';

    // Base prompt template
    const createSinglePrompt = () => {
      return `Generate an image: Create a professional ${selectedFormat?.label} image for ${selectedCategory?.label} brand.

**BRAND DNA:**
- Brand Category: ${selectedCategory?.label}
- Brand Style: ${styleLabels}
- Brand Colors: ${brandDNA.colors.join(', ')}
- Target Audience: ${brandDNA.targetAudience}${genderContext}

**CONTENT FOCUS:**
${contentToAnalyze}

**TEMPLATE TYPE:**
${selectedTemplate?.label} - ${templatePreset[templateType]}

**FORMAT OPTIMIZATION:**
${platformPreset[platform]}

**VISUAL STYLE:**
${combinedStyleDescription}

**COLOR PALETTE:**
${colorPaletteText}

**COMPOSITION:**
${styleOverride || combinedStyleDescription}
${headline ? `\n- Main Headline: "${headline}" (large, prominent, readable)` : ''}
${subtext ? `- Subtext: "${subtext}" (supporting text)` : ''}
${cta ? `- Call-to-Action: "${cta}" (clear, actionable)` : ''}
${!hideContact && (brandDNA.contactLine || brandDNA.contactPhone || (brandDNA.socialAccounts && brandDNA.socialAccounts.length > 0)) ? `\n\n**CONTACT INFO (display in image):**` : ''}
${!hideContact && brandDNA.contactLine ? `\n- Line ID: ${brandDNA.contactLine}` : ''}
${!hideContact && brandDNA.contactPhone ? `\n- Phone: ${brandDNA.contactPhone}` : ''}
${!hideContact && brandDNA.socialAccounts && brandDNA.socialAccounts.length > 0 ? (() => {
  // Group accounts by username
  const groupedByUsername = brandDNA.socialAccounts.reduce((acc, account) => {
    if (!acc[account.username]) {
      acc[account.username] = [];
    }
    acc[account.username].push(account.platform);
    return acc;
  }, {} as Record<string, string[]>);
  
  // Format: Show platforms as icons with username
  return '\n- Social Media: ' + Object.entries(groupedByUsername).map(([username, platforms]) => {
    const platformNames = platforms.map(p => {
      const platformInfo = socialPlatforms.find(sp => sp.id === p);
      return platformInfo?.label || p;
    }).join(', ');
    return `${username} (show ${platformNames} icons, NOT text)`;
  }).join('; ');
})() : ''}

**MOOD & ATMOSPHERE:**
${styleLabels} aesthetic

**CREATIVE DIRECTION:**
${creativityPresets[creativeStrength]}
${referenceInstructions}
${nextImageInstructions}

**TECHNICAL SPECS:**
- Aspect Ratio: ${selectedFormat?.ratio}
- High resolution, professional quality
- Optimized for ${selectedFormat?.description}
${noAIModel ? '- ⚠️ NO AI models/people - Focus on product/service only' : '- May include human subjects if appropriate'}

**NEGATIVE PROMPT:**
${negativePrompt}

**OUTPUT:**
Professional social media image ready for ${selectedFormat?.label}, maintaining brand consistency and visual excellence.`;
    };

    // Generate single prompt
    const prompt = createSinglePrompt();

    setGeneratedPrompt(prompt);
    setLastGeneratedContent(fullContent); // บันทึกเนื้อหาที่ใช้สร้าง prompt
    setLastNoAIModel(noAIModel); // บันทึกสถานะ checkbox ไม่ต้องการนางแบบ AI
    setIsGenerating(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAll = () => {
    setPlatform('');
    setTemplateType('');
    setQuickTopic('');
    setFullContent('');
    setHeadline('');
    setSubtext('');
    setCta('');
    setGeneratedPrompt('');
    setLastGeneratedContent(''); // รีเซ็ตเนื้อหาครั้งก่อน
    setLastNoAIModel(false); // รีเซ็ตสถานะ checkbox
    setNoAIModel(false); // รีเซ็ต checkbox เน้นสินค้า/โลโก้
    setHideContact(false); // รีเซ็ต checkbox ปิด Contact
    setShowAdvanced(false);
  };

  // Loading screen
  if (isLoadingBrandDNA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-orange-950 to-purple-950">
        <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background with gradient overlay */}
      <div className="fixed inset-0 z-0">
        {/* Mobile background */}
        <div 
          className="absolute inset-0 bg-center bg-no-repeat bg-cover md:hidden"
          style={{
            backgroundImage: 'url(/Coursedee-bg-mobile-V2.jpg)',
            backgroundColor: '#0f172a'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-orange-950/50 to-purple-950/70" />
        </div>
        {/* Desktop background */}
        <div 
          className="hidden md:block absolute inset-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(/Coursedee-bg-web-V2.jpg)',
            backgroundColor: '#0f172a'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-orange-950/50 to-purple-950/70" />
        </div>
      </div>
      
      <div className="relative z-10 min-h-screen p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative">
              <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-400 to-orange-500 mb-2 flex items-center justify-center gap-3">
                <Wand2 className="w-10 h-10 text-orange-400" />
                AIForYou V2
                <Sparkles className="w-10 h-10 text-pink-400" />
              </h1>
              
              {/* User Info - Top Right */}
              <div className="absolute right-0 top-0 flex items-center gap-2">
                <div className="flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm px-2 md:px-3 py-1 md:py-1.5 rounded-full border border-orange-400/30">
                  <div className="w-6 h-6 md:w-7 md:h-7 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                    {currentUsername[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-white font-semibold text-xs md:text-sm">{currentUsername}</span>
                </div>
                <a
                  href="https://aiforyou.web.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full border border-orange-400/30 transition-all"
                  title="กลับหน้าแรก"
                >
                  <Home className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </a>
              </div>
            </div>
            <p className="text-lg text-slate-300 font-medium">AI Prompt Operating System</p>
            <p className="text-sm text-slate-400 mt-1">สร้าง Prompts สำหรับทุก AI Image Generators</p>
          </div>

          {/* Loading State */}
          {isLoadingBrandDNA && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-orange-400 animate-spin mb-4" />
              <p className="text-slate-300 text-lg">กำลังโหลด Brand DNA...</p>
            </div>
          )}

          {/* Brand DNA Setup Section */}
          {!isLoadingBrandDNA && showBrandSetup && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-orange-500/10 via-pink-500/10 to-orange-500/10 border border-orange-400/30 rounded-2xl p-6 mb-6">
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <Palette className="w-7 h-7 text-orange-400" />
                BRAND DNA SETUP
              </h2>
              <p className="text-slate-300">กรุณาตั้งค่า Brand DNA ก่อนใช้งาน (ทำครั้งเดียว)</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Brand Category */}
              <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl p-6 border border-orange-400/30 shadow-xl shadow-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-orange-400" />
                  Brand Category
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {brandCategories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setBrandDNA({...brandDNA, brandCategory: category.id})}
                        className={`p-4 rounded-xl border-2 transition-all transform hover:scale-[1.02] ${
                          brandDNA.brandCategory === category.id
                            ? `bg-gradient-to-r ${category.color} border-transparent shadow-lg`
                            : 'bg-slate-900/50 border-orange-400/20 hover:border-orange-400/50'
                        }`}
                      >
                        <Icon className={`w-6 h-6 mx-auto mb-2 ${brandDNA.brandCategory === category.id ? 'text-white' : 'text-slate-400'}`} />
                        <div className={`font-semibold text-xs ${brandDNA.brandCategory === category.id ? 'text-white' : 'text-slate-300'}`}>
                          {category.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Brand Style */}
              <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl p-6 border border-pink-400/30 shadow-xl shadow-pink-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-pink-400" />
                  Brand Style
                </h3>
                <p className="text-xs text-slate-400 mb-3">เลือกได้สูงสุด 2 แบบ</p>
                <div className="grid grid-cols-2 gap-2">
                  {brandStyles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => toggleBrandStyle(style.id)}
                      className={`p-3 rounded-xl border-2 transition-all transform hover:scale-[1.02] ${
                        brandDNA.brandStyles.includes(style.id)
                          ? `bg-gradient-to-r ${style.color} border-transparent shadow-lg text-white`
                          : 'bg-slate-900/50 border-pink-400/20 hover:border-pink-400/50 text-slate-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">{style.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Colors */}
              <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl p-6 border border-orange-400/30 shadow-xl shadow-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-orange-400" />
                  Brand Colors
                </h3>
                
                {/* Color Circles */}
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  {brandDNA.colors.map((color, index) => (
                    <div key={index} className="relative flex flex-col items-center gap-1">
                      <button
                        onClick={() => setShowColorPicker(showColorPicker === index ? null : index)}
                        className={`rounded-full border-4 transition-all hover:scale-110 ${
                          index === 0 
                            ? 'w-16 h-16 border-orange-400/50' 
                            : index === 1 
                            ? 'w-14 h-14 border-pink-400/50' 
                            : 'w-12 h-12 border-slate-400/30'
                        }`}
                        style={{ backgroundColor: color }}
                        title={index === 0 ? 'Primary Color' : index === 1 ? 'Secondary Color' : `Accent ${index - 1}`}
                      />
                      {index > 1 && (
                        <button
                          onClick={() => removeColor(index)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center hover:bg-red-600 transition-all"
                        >
                          ×
                        </button>
                      )}
                      <span className={`text-xs font-semibold ${
                        index === 0 ? 'text-orange-400' : index === 1 ? 'text-pink-400' : 'text-slate-400'
                      }`}>
                        {index === 0 ? '60%' : index === 1 ? '30%' : '10%'}
                      </span>
                    </div>
                  ))}
                  
                  {brandDNA.colors.length < 6 && (
                    <button
                      onClick={addColor}
                      className="w-12 h-12 rounded-full border-2 border-dashed border-orange-400/50 flex items-center justify-center text-orange-400 hover:bg-orange-400/10 transition-all"
                    >
                      +
                    </button>
                  )}
                </div>

                {/* Color Picker Popup */}
                {showColorPicker !== null && (
                  <div className="bg-slate-900/95 backdrop-blur-xl rounded-xl p-4 border border-orange-400/30 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-300">
                        {showColorPicker === 0 ? 'Primary Color' : showColorPicker === 1 ? 'Secondary Color' : `Accent ${showColorPicker - 1}`}
                      </span>
                      <button
                        onClick={() => setShowColorPicker(null)}
                        className="text-slate-400 hover:text-white"
                      >
                        ×
                      </button>
                    </div>
                    
                    {/* Hex Input */}
                    <div className="mb-3">
                      <input
                        type="text"
                        value={brandDNA.colors[showColorPicker]}
                        onChange={(e) => updateColor(showColorPicker, e.target.value)}
                        className="w-full px-4 py-2 bg-slate-800/50 border border-orange-400/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all font-mono text-sm"
                        placeholder="#FF69B4"
                      />
                    </div>

                    {/* Color Input */}
                    <div className="mb-3">
                      <input
                        type="color"
                        value={brandDNA.colors[showColorPicker]}
                        onChange={(e) => updateColor(showColorPicker, e.target.value)}
                        className="w-full h-32 rounded-lg cursor-pointer border-2 border-orange-400/30"
                      />
                    </div>

                    {/* Preset Colors */}
                    <div className="grid grid-cols-8 gap-2">
                      {['#FF69B4', '#FF6B6B', '#FFA500', '#FFD700', '#4ECDC4', '#45B7D1', '#A78BFA', '#FFFFFF', 
                        '#F06292', '#EF5350', '#FF9800', '#FFC107', '#26A69A', '#42A5F5', '#7E57C2', '#000000'].map((presetColor) => (
                        <button
                          key={presetColor}
                          onClick={() => updateColor(showColorPicker, presetColor)}
                          className="w-8 h-8 rounded-lg border-2 border-slate-600 hover:border-orange-400 transition-all"
                          style={{ backgroundColor: presetColor }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-xs text-slate-400 mt-2 space-y-1">
                  <p className="font-medium text-slate-300">สัดส่วนการใช้สี:</p>
                  <p>• สีที่ 1 (ใหญ่สุด) = สีหลัก 60% • สีที่ 2 (กลาง) = สีรอง 30% • สีที่ 3+ (เล็ก) = สีเสริม 10%</p>
                  <p className="text-slate-500">คลิกที่วงกลมเพื่อเปลี่ยนสี • เพิ่มได้สูงสุด 6 สี</p>
                </div>
              </div>

              {/* Target Audience */}
              <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl p-6 border border-pink-400/30 shadow-xl shadow-pink-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-400" />
                  Target Audience
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Audience Type *</label>
                    <div className="grid grid-cols-3 gap-2">
                      {targetAudiences.map((audience) => (
                        <button
                          key={audience.id}
                          onClick={() => setBrandDNA({...brandDNA, targetAudience: audience.id})}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            brandDNA.targetAudience === audience.id
                              ? 'bg-gradient-to-r from-pink-500 to-orange-500 border-transparent shadow-lg text-white'
                              : 'bg-slate-900/50 border-pink-400/20 hover:border-pink-400/50 text-slate-300'
                          }`}
                        >
                          <div className="font-semibold text-sm">{audience.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Gender (Optional)</label>
                    <div className="flex gap-2">
                      {targetGenders.map((gender) => (
                        <button
                          key={gender.id}
                          onClick={() => setBrandDNA({...brandDNA, targetGender: gender.id})}
                          className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all text-sm ${
                            brandDNA.targetGender === gender.id
                              ? 'bg-gradient-to-r from-pink-500 to-orange-500 border-transparent shadow-md text-white font-semibold'
                              : 'bg-slate-900/50 border-pink-400/20 hover:border-pink-400/50 text-slate-400'
                          }`}
                        >
                          {gender.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl p-6 border border-orange-400/30 shadow-xl shadow-orange-500/20 md:col-span-2">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-orange-400" />
                  ช่องทางติดต่อ (ไม่บังคับ)
                </h3>
                <p className="text-xs text-slate-400 mb-4">ข้อมูลนี้จะถูกใส่ในภาพที่สร้างโดยอัตโนมัติ</p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Line ID</label>
                    <input
                      type="text"
                      value={brandDNA.contactLine || ''}
                      onChange={(e) => setBrandDNA({...brandDNA, contactLine: e.target.value})}
                      placeholder="@yourline"
                      className="w-full px-4 py-3 bg-slate-900/50 border border-orange-400/30 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">เบอร์โทร</label>
                    <input
                      type="text"
                      value={brandDNA.contactPhone || ''}
                      onChange={(e) => setBrandDNA({...brandDNA, contactPhone: e.target.value})}
                      placeholder="08X-XXX-XXXX"
                      className="w-full px-4 py-3 bg-slate-900/50 border border-orange-400/30 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all"
                    />
                  </div>
                </div>

                {/* Social Media Accounts */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">Social Media Accounts</label>
                  
                  {/* Selected Accounts */}
                  {brandDNA.socialAccounts && brandDNA.socialAccounts.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {brandDNA.socialAccounts.map((account, index) => {
                        const platformInfo = socialPlatforms.find(p => p.id === account.platform);
                        return (
                          <div key={index} className="flex items-center gap-2 bg-slate-900/50 border border-orange-400/30 rounded-lg px-3 py-2">
                            <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded">
                              {platformInfo?.shortLabel || account.platform}
                            </span>
                            <span className="text-white text-sm flex-1">{account.username}</span>
                            <button
                              onClick={() => removeSocialAccount(index)}
                              className="text-red-400 hover:text-red-300 transition-all"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add New Platform */}
                  <div className="bg-slate-900/30 rounded-xl p-4 border border-orange-400/20">
                    <p className="text-xs text-slate-400 mb-3">เลือก Platform (เลือกได้หลายตัว):</p>
                    
                    {/* Platform Buttons */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {socialPlatforms.map((platform) => {
                        const isSelected = selectedPlatforms.includes(platform.id);
                        return (
                          <button
                            key={platform.id}
                            onClick={() => togglePlatform(platform.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              isSelected
                                ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
                                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                            }`}
                          >
                            {platform.shortLabel}
                          </button>
                        );
                      })}
                    </div>

                    {/* Username Input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={socialUsername}
                        onChange={(e) => setSocialUsername(e.target.value)}
                        placeholder="ชื่อเพจ / username"
                        className="flex-1 px-4 py-2 bg-slate-800/50 border border-orange-400/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && addSocialAccount()}
                      />
                      <button
                        onClick={addSocialAccount}
                        disabled={selectedPlatforms.length === 0 || !socialUsername.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-semibold rounded-lg hover:from-orange-400 hover:to-pink-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        เพิ่ม
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">💡 เลือกหลาย Platform พร้อมกัน แล้วใส่ชื่อเดียวกันได้</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={saveBrandDNA}
                className="bg-gradient-to-r from-orange-500 via-pink-500 to-orange-600 text-white font-bold px-8 py-4 rounded-xl hover:from-orange-400 hover:via-pink-400 hover:to-orange-500 transition-all shadow-lg shadow-orange-500/50 flex items-center gap-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                บันทึก Brand DNA
              </button>
            </div>
          </div>
        )}

        {/* Prompt Generator Section */}
        {!isLoadingBrandDNA && hasBrandDNA && !showBrandSetup && (
          <div>
            <div className="bg-gradient-to-r from-orange-500/10 via-pink-500/10 to-orange-500/10 border border-orange-400/30 rounded-xl p-3 mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-orange-400" />
                  PROMPT GENERATOR
                </h2>
                <p className="text-xs text-slate-400">Brand: {brandCategories.find(b => b.id === brandDNA.brandCategory)?.label}</p>
              </div>
              <button
                onClick={editBrandDNA}
                className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
              >
                <Settings className="w-3 h-3" />
                แก้ไข Brand DNA
              </button>
            </div>

            <div className="space-y-6">
              {/* Step 1: Select Platform */}
              <div className="bg-gradient-to-br from-slate-950/80 to-slate-900/80 backdrop-blur-xl rounded-3xl p-6 border border-orange-400/20 shadow-2xl shadow-orange-500/5">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="bg-gradient-to-br from-orange-500 to-pink-500 p-2.5 rounded-2xl">
                    <Monitor className="w-6 h-6 text-white" />
                  </div>
                  เลือก Platform (ไม่บังคับ)
                </h3>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {contentPlatforms.map((plat) => {
                    const Icon = plat.icon;
                    const isSelected = selectedSocialPlatform === plat.id;
                    return (
                      <button
                        key={plat.id}
                        onClick={() => {
                          setSelectedSocialPlatform(plat.id);
                          setPlatform('square');
                        }}
                        className={`group relative overflow-hidden rounded-2xl p-4 transition-all duration-300 ${
                          isSelected
                            ? 'bg-gradient-to-br from-orange-500 to-pink-500 scale-105 shadow-xl shadow-orange-500/50'
                            : 'bg-slate-900/60 hover:bg-slate-800/80 border border-orange-400/20 hover:border-orange-400/40 hover:scale-105'
                        }`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br from-orange-400/0 to-pink-400/0 group-hover:from-orange-400/10 group-hover:to-pink-400/10 transition-all duration-300 ${isSelected ? 'opacity-0' : ''}`}></div>
                        
                        <div className="relative flex flex-col items-center gap-2">
                          <Icon className={`w-7 h-7 transition-all duration-300 ${
                            isSelected ? 'text-white' : 'text-orange-400 group-hover:text-orange-300'
                          }`} />
                          <div className={`text-xs font-semibold ${
                            isSelected ? 'text-white' : 'text-slate-200 group-hover:text-white'
                          }`}>
                            {plat.label}
                          </div>
                        </div>
                        
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Clear Platform Button */}
                {selectedSocialPlatform && (
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => {
                        setSelectedSocialPlatform('');
                        setPlatform('square');
                      }}
                      className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      ล้างการเลือก (ใช้ 1:1 ทุก Platform)
                    </button>
                  </div>
                )}
              </div>

              {/* Step 2: Select Format (แสดงเมื่อเลือก Platform) */}
              {selectedSocialPlatform && platformFormats[selectedSocialPlatform] && (
                <div className="bg-gradient-to-br from-slate-950/80 to-slate-900/80 backdrop-blur-xl rounded-3xl p-6 border border-orange-400/20 shadow-2xl shadow-orange-500/5">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <div className="bg-gradient-to-br from-orange-500 to-pink-500 p-2.5 rounded-2xl">
                      <ImageIcon className="w-6 h-6 text-white" />
                    </div>
                    เลือกขนาด
                  </h3>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {platformFormats[selectedSocialPlatform].map((format) => {
                      const Icon = format.icon;
                      const isSelected = platform === format.id;
                      return (
                        <div key={format.id} className="relative group/tooltip">
                          <button
                            onClick={() => setPlatform(format.id)}
                            className={`w-full group relative overflow-hidden rounded-2xl p-4 transition-all duration-300 ${
                              isSelected
                                ? 'bg-gradient-to-br from-orange-500 to-pink-500 scale-105 shadow-xl shadow-orange-500/50'
                                : 'bg-slate-900/60 hover:bg-slate-800/80 border border-orange-400/20 hover:border-orange-400/40 hover:scale-105'
                            }`}
                          >
                            <div className={`absolute inset-0 bg-gradient-to-br from-orange-400/0 to-pink-400/0 group-hover:from-orange-400/10 group-hover:to-pink-400/10 transition-all duration-300 ${isSelected ? 'opacity-0' : ''}`}></div>
                            
                            <div className="relative flex flex-col items-center gap-2">
                              <Icon className={`w-8 h-8 transition-all duration-300 ${
                                isSelected ? 'text-white' : 'text-orange-400 group-hover:text-orange-300'
                              }`} />
                              <div className="text-center">
                                <div className={`text-sm font-bold mb-1 ${
                                  isSelected ? 'text-white' : 'text-slate-200 group-hover:text-white'
                                }`}>
                                  {format.ratio !== 'multi' ? format.ratio : '✨'}
                                </div>
                                <div className={`text-xs leading-tight ${
                                  isSelected ? 'text-white/90' : 'text-slate-400 group-hover:text-slate-300'
                                }`}>
                                  {format.label}
                                </div>
                              </div>
                            </div>
                            
                            {isSelected && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle2 className="w-5 h-5 text-white" />
                              </div>
                            )}
                          </button>
                          
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none shadow-xl border border-orange-400/30">
                            {format.description}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 2: Template Type */}
              <div className="bg-gradient-to-br from-slate-950/80 to-slate-900/80 backdrop-blur-xl rounded-3xl p-6 border border-pink-400/20 shadow-2xl shadow-pink-500/5">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="bg-gradient-to-br from-pink-500 to-purple-500 p-2.5 rounded-2xl">
                    <Layers className="w-6 h-6 text-white" />
                  </div>
                  เลือกประเภท
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {templateTypes.map((template) => {
                    const Icon = template.icon;
                    const isSelected = templateType === template.id;
                    return (
                      <div key={template.id} className="relative group/tooltip">
                        <button
                          onClick={() => setTemplateType(template.id)}
                          className={`w-full group relative overflow-hidden rounded-2xl p-4 transition-all duration-300 ${
                            isSelected
                              ? `bg-gradient-to-br ${template.color} scale-105 shadow-xl shadow-pink-500/50`
                              : 'bg-slate-900/60 hover:bg-slate-800/80 border border-pink-400/20 hover:border-pink-400/40 hover:scale-105'
                          }`}
                        >
                          {/* Animated Background */}
                          <div className={`absolute inset-0 bg-gradient-to-br from-pink-400/0 to-purple-400/0 group-hover:from-pink-400/10 group-hover:to-purple-400/10 transition-all duration-300 ${isSelected ? 'opacity-0' : ''}`}></div>
                          
                          <div className="relative flex flex-col items-center gap-2">
                            <Icon className={`w-7 h-7 transition-all duration-300 ${
                              isSelected ? 'text-white' : 'text-pink-400 group-hover:text-pink-300'
                            }`} />
                            <div className={`text-sm font-semibold text-center ${
                              isSelected ? 'text-white' : 'text-slate-200 group-hover:text-white'
                            }`}>
                              {template.label}
                            </div>
                          </div>
                          
                          {/* Selected Indicator */}
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle2 className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </button>
                        
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none shadow-xl border border-pink-400/30">
                          {template.description}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-slate-900"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Step 3: Content Input */}
              <div className="bg-gradient-to-br from-slate-950/80 to-slate-900/80 backdrop-blur-xl rounded-3xl p-6 border border-orange-400/20 shadow-2xl shadow-orange-500/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-gradient-to-br from-orange-500 to-pink-500 p-2.5 rounded-2xl">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    เนื้อหาที่ต้องการ
                  </h3>
                  {fullContent && (
                    <button
                      onClick={() => setFullContent('')}
                      className="flex items-center gap-1.5 bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg transition-all text-sm font-medium"
                    >
                      <X className="w-4 h-4" />
                      Clear
                    </button>
                  )}
                </div>
                <textarea
                  value={fullContent}
                  onChange={(e) => setFullContent(e.target.value)}
                  placeholder="ใส่เนื้อหาที่ต้องการสร้างภาพ... (หากต้องการสร้างภาพต่อไป ให้พิมพ์ว่า 'ภาพต่อไป' ใน ChatGPT)"
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-orange-400/30 rounded-xl text-base text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all"
                />
                
                {/* Pill Buttons */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {/* Pill: เน้นสินค้า/โลโก้ เท่านั้น */}
                  <button
                    type="button"
                    onClick={() => setNoAIModel(!noAIModel)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      noAIModel
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:border-orange-400/40 hover:text-slate-300'
                    }`}
                  >
                    เน้นสินค้า/โลโก้ เท่านั้น
                  </button>
                  
                  {/* Pill: ปิด Contact */}
                  <button
                    type="button"
                    onClick={() => setHideContact(!hideContact)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      hideContact
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:border-orange-400/40 hover:text-slate-300'
                    }`}
                  >
                    ปิด Contact
                  </button>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generatePrompt}
                disabled={isGenerating}
                className="w-full bg-gradient-to-r from-orange-500 via-pink-500 to-orange-600 text-white font-bold py-4 rounded-xl hover:from-orange-400 hover:via-pink-400 hover:to-orange-500 transition-all shadow-lg shadow-orange-500/50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-6 h-6" />
                    Generate Prompt
                  </>
                )}
              </button>

              {/* Output Panel */}
              <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl p-6 border border-pink-400/30 shadow-lg shadow-pink-500/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-pink-400" />
                    Generated Prompt
                  </h3>
                  {generatedPrompt && (
                    <button
                      onClick={copyToClipboard}
                      className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all text-sm font-semibold"
                    >
                      {copied ? (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          Copy
                        </>
                      )}
                    </button>
                  )}
                </div>

                {generatedPrompt ? (
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-pink-400/20 max-h-96 overflow-y-auto">
                    <pre className="text-slate-300 text-sm whitespace-pre-wrap font-mono leading-relaxed">
                      {generatedPrompt}
                    </pre>
                  </div>
                ) : (
                  <div className="bg-slate-900/50 rounded-xl p-12 border border-pink-400/20 border-dashed flex flex-col items-center justify-center text-center min-h-[300px]">
                    <Sparkles className="w-16 h-16 text-slate-600 mb-4" />
                    <p className="text-slate-400 text-lg font-medium mb-2">
                      Ready to Create
                    </p>
                    <p className="text-slate-600 text-sm">
                      Describe your content and let AI handle the rest.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CoursedeeAIV2;
